import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectsAPI } from '../../../services/api';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { STORAGE_PATHS } from '../../../config/constants';
import './AdminProjectForm.css';

function AdminProjectForm() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        status: 'active',
        imageUrl: ''
    });
    const [loading, setLoading] = useState(false);
    const [imageFile, setImageFile] = useState(null);
    const storage = getStorage();

    useEffect(() => {
        if (projectId) {
            fetchProject();
        }
    }, [projectId]);

    const fetchProject = async () => {
        try {
            const response = await projectsAPI.getProject(projectId);
            setFormData(response);
        } catch (error) {
            alert('Error fetching project details');
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImageChange = (e) => {
        if (e.target.files[0]) {
            setImageFile(e.target.files[0]);
        }
    };

    const uploadImage = async () => {
        if (!imageFile) return null;

        try {
            const imagePath = `${STORAGE_PATHS.PROJECTS || 'projects'}/${Date.now()}_${imageFile.name}`;
            const fileRef = storageRef(storage, imagePath);
            
            // Upload the file
            const uploadResult = await uploadBytes(fileRef, imageFile);
            
            // Get download URL
            const downloadURL = await getDownloadURL(uploadResult.ref);
            
            return downloadURL;
        } catch (error) {
            throw new Error('Failed to upload project image: ' + error.message);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);

            let imageUrl = formData.imageUrl;
            if (imageFile) {
                try {
                    imageUrl = await uploadImage();
                } catch (uploadError) {
                    alert('Error uploading image: ' + uploadError.message);
                    setLoading(false);
                    return;
                }
            }

            const projectData = {
                ...formData,
                imageUrl
            };

            if (projectId) {
                await projectsAPI.updateProject(projectId, projectData);
            } else {
                await projectsAPI.createProject(projectData);
            }
            
            navigate('/admin-dashboard');
        } catch (error) {
            alert('Error saving project: ' + (error.message || 'Unknown error'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="admin-project-form">
            <h2>{projectId ? 'Edit Project' : 'Create New Project'}</h2>
            <form onSubmit={handleSubmit}>
                <div className="form-group">
                    <label htmlFor="title">Title</label>
                    <input
                        type="text"
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="description">Description</label>
                    <textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        required
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="status">Status</label>
                    <select
                        id="status"
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                    >
                        <option value="active">Active</option>
                        <option value="completed">Completed</option>
                        <option value="pending">Pending</option>
                    </select>
                </div>

                <div className="form-group">
                    <label htmlFor="image">Project Image</label>
                    <input
                        type="file"
                        id="image"
                        accept="image/*"
                        onChange={handleImageChange}
                    />
                    {formData.imageUrl && (
                        <div className="image-preview">
                            <img src={formData.imageUrl} alt="Project preview" />
                        </div>
                    )}
                </div>

                <button 
                    type="submit" 
                    className="submit-button"
                    disabled={loading}
                >
                    {loading ? 'Saving...' : (projectId ? 'Update Project' : 'Create Project')}
                </button>
            </form>
        </div>
    );
}

export default AdminProjectForm;
